import { useMemo } from "react";
import { useTheme } from "@mui/material";
import type { FilterState } from "../components/FilterDrawer";
import type { DateRange } from "../components/DurationSelector/types";
import { buildFilterParams } from "../../features/admin/dashboards/shared/utils";
import { getComparisonGroupColor } from "../utils/comparisonSeries";
import type { ComparisonGroup } from "./useComparisonGroups";

/** Short human label for a range, e.g. "1–31 Mar" or "Mar – May 2026". */
const rangeLabel = (range: DateRange): string => {
  const [start, end] = range;
  if (!start || !end) return "Comparison";
  const sameMonth = start.isSame(end, "month");
  const sameYear = start.isSame(end, "year");
  if (sameMonth) return start.format("MMM YYYY");
  if (sameYear) return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  return `${start.format("D MMM YY")} – ${end.format("D MMM YY")}`;
};

export interface UsePeriodComparisonGroupsResult {
  groups: ComparisonGroup[];
  isComparing: boolean;
}

/**
 * Period comparison: render the SAME cohort over two time windows side by side,
 * reusing the group-comparison machinery (ComparisonColumns / useComparisonTrends).
 *
 * In average mode (or when the compare range is incomplete) it returns a single
 * group with the unchanged filter params — byte-for-byte the default behaviour.
 * When enabled with a complete compare range it returns two groups: the selected
 * period and the comparison period, each carrying the same filters but a
 * different date range.
 */
export const usePeriodComparisonGroups = (
  filters: FilterState,
  dateRange: DateRange,
  compareRange: DateRange,
  clientId: string | null | undefined,
  enabled: boolean
): UsePeriodComparisonGroupsResult => {
  const theme = useTheme();

  return useMemo(() => {
    const baseParams = buildFilterParams(filters, dateRange, clientId);
    const hasCompare = Boolean(compareRange[0] && compareRange[1]);
    // A compare range identical to the primary range would render two identical
    // columns — treat it as "no comparison" so the dashboard stays in average mode.
    const sameAsPrimary =
      hasCompare &&
      compareRange[0]?.isSame(dateRange[0], "day") &&
      compareRange[1]?.isSame(dateRange[1], "day");

    if (!enabled || !hasCompare || sameAsPrimary) {
      return {
        groups: [
          {
            key: "all",
            label: "All",
            color: theme.palette.primary.main,
            filterParams: baseParams,
          },
        ],
        isComparing: false,
      };
    }

    const groups: ComparisonGroup[] = [
      {
        key: "current",
        label: rangeLabel(dateRange),
        color: getComparisonGroupColor(0, theme),
        filterParams: baseParams,
      },
      {
        key: "compare",
        label: rangeLabel(compareRange),
        color: getComparisonGroupColor(1, theme),
        // Same filters, different window: swap the date range for the baseline.
        filterParams: buildFilterParams(filters, compareRange, clientId),
      },
    ];

    return { groups, isComparing: true };
    // Ranges compared by epoch to avoid re-running on identical dayjs instances.
  }, [
    filters,
    dateRange[0]?.valueOf(),
    dateRange[1]?.valueOf(),
    compareRange[0]?.valueOf(),
    compareRange[1]?.valueOf(),
    clientId,
    enabled,
    theme,
  ]);
};

// Re-exported for callers that build labels elsewhere.
export const periodRangeLabel = rangeLabel;
